import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Paper, Typography, Button, TextField, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, IconButton, Grid, MenuItem, Chip,
  Collapse, Alert, FormControl, InputLabel, Select, Tooltip, Card,
  CardContent, Divider, FormControlLabel, Checkbox, CircularProgress,
  Stack, Fab, useMediaQuery, useTheme
} from '@mui/material';
import {
  Add, Edit, Delete, ExpandMore, ExpandLess, Receipt, Download,
  CheckCircle, Cancel, Undo, PictureAsPdf, FilterList, PhotoCamera, InfoOutlined
} from '@mui/icons-material';

import { useConfirmDialog } from './ConfirmDialog';
import { useAuth } from '../context/AuthContext';
import ExpenseFormDialog from './dialogs/ExpenseFormDialog';
import ExpenseItemDialog from './dialogs/ExpenseItemDialog';
import StatusChip from './StatusChip';
import {
  getExpenses, getExpense, createExpense, updateExpense, deleteExpense,
  addExpenseItem, updateExpenseItem, deleteExpenseItem, uploadReceipt, deleteReceipt, openReceipt,
  exportExpenses, exportExpensesDetailed, getEmployees, exportExpensePdf
} from '../api';

const EXPENSE_TYPES = [
  { value: 'trip', label: 'Trip/Travel' },
  { value: 'supplies', label: 'Supplies' },
  { value: 'equipment', label: 'Equipment' },
  { value: 'training', label: 'Training' },
  { value: 'meals', label: 'Meals & Entertainment' },
  { value: 'other', label: 'Other' }
];

const ITEM_CATEGORIES = [
  { value: 'mileage', label: 'Mileage' },
  { value: 'fuel', label: 'Fuel' },
  { value: 'lodging', label: 'Lodging' },
  { value: 'meals', label: 'Meals' },
  { value: 'parking', label: 'Parking' },
  { value: 'tolls', label: 'Tolls' },
  { value: 'airfare', label: 'Airfare' },
  { value: 'transit', label: 'Transit' },
  { value: 'rental_car', label: 'Rental Car' },
  { value: 'supplies', label: 'Supplies' },
  { value: 'equipment', label: 'Equipment' },
  { value: 'software', label: 'Software' },
  { value: 'registration', label: 'Registration/Fees' },
  { value: 'other', label: 'Other' }
];

// Plan 068: shown beside the Paid checkbox of an expense a payroll run reimbursed
const PAYROLL_LOCK_TEXT = 'Reimbursed by a payroll run. Its status and Paid tick are locked until that run is voided on the Payroll tab; voiding puts it back to Approved and unpaid so a replacement run can pay it.';

// Plan 086: shown beside the receipt Delete icon of an expense a payroll run reimbursed
const RECEIPT_LOCK_TEXT = (runId) => `Reimbursed by a payroll run (#${runId}). Receipts are the substantiation for that payment and cannot be deleted until the run is voided on the Payroll tab.`;

// Plan 097: receipts of an expense ticked Paid by hand (no payroll run)
const RECEIPT_PAID_LOCK_TEXT = 'Marked Paid (reimbursed outside payroll). Receipts are the substantiation for that reimbursement and cannot be deleted while the expense is marked Paid. Untick Paid (on a phone: Mark as Paid) only if it was marked Paid by mistake.';
// Shown beside a hand-ticked Paid checkbox
const PAID_TICK_LOCK_TEXT = 'Marked Paid by hand. While ticked, this expense cannot be deleted, items cannot be added or deleted, item amounts cannot be changed, and its receipts cannot be deleted or replaced. Untick only if it was marked Paid by mistake: that clears the Paid date and payer and is recorded in the Transaction Log.';
// Gitea #136: shown on a reviewer's own rows instead of Approve / Reject / Reverse and the Paid checkbox
const SELF_REVIEW_TEXT = 'This is your own expense. Only your supervisor or a tenant admin can approve, reject, reverse or mark it Paid.';
// Tooltip for a receipt-locked expense, or null when its receipts are not locked. A payroll run wins.
const receiptLockText = (expense) => {
  if (expense.payroll_run_id) return RECEIPT_LOCK_TEXT(expense.payroll_run_id);
  if (expense.is_paid === true) return RECEIPT_PAID_LOCK_TEXT;
  return null;
};

// Legacy-only receipt (no receipts rows) on a reimbursed expense: the server refuses a replacing upload
const receiptReplaceLocked = (expense, item) =>
  !!receiptLockText(expense) && !!item.receipt_filename && !(item.receipts && item.receipts.length > 0);

// Plan 104: items and the expense itself are frozen while ticked Paid by hand (no payroll run)
const handPaid = (expense) => !!expense && expense.is_paid === true && !expense.payroll_run_id;
const ITEM_PAID_LOCK_TEXT = 'Marked Paid (reimbursed outside payroll). Its line items are the record of that reimbursement, so items cannot be added or deleted and amounts cannot be changed while it is marked Paid. Untick Paid (on a phone: Mark as Paid) only if it was marked Paid by mistake.';
const EXPENSE_PAID_DELETE_TEXT = 'Marked Paid (reimbursed outside payroll). The expense and its receipts are the record of that reimbursement and cannot be deleted while it is marked Paid. Untick Paid only if it was marked Paid by mistake.';

function Expenses() {
  const { user } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { showConfirm, ConfirmDialogComponent } = useConfirmDialog();

  const [expenses, setExpenses] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Filters
  const [filterEmployee, setFilterEmployee] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterType, setFilterType] = useState('');
  const [filterStartDate, setFilterStartDate] = useState('');
  const [filterEndDate, setFilterEndDate] = useState('');
  const [showFilters, setShowFilters] = useState(false);

  // Dialogs
  const [openDialog, setOpenDialog] = useState(false);
  const [openItemDialog, setOpenItemDialog] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editItemMode, setEditItemMode] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [currentExpense, setCurrentExpense] = useState(null);
  const [expandedId, setExpandedId] = useState(null);

  // Form data
  const [formData, setFormData] = useState({
    start_date: new Date().toISOString().split('T')[0],
    end_date: new Date().toISOString().split('T')[0],
    title: '',
    description: '',
    expense_type: 'other'
  });

  const [itemFormData, setItemFormData] = useState({
    item_date: new Date().toISOString().split('T')[0],
    category: 'other',
    description: '',
    amount: '',
    notes: '',
    payment_method: 'personal'
  });

  const isAdmin = user?.role === 'admin';
  const isManager = user?.role === 'manager';
  const canManageExpenses = isAdmin || isManager;
  // Gitea #136 (Joshua 2026-10-22): nobody approves, rejects, reverses or marks Paid their own
  // expense except a built-in tenant admin (isAdmin) - the time-entry / time-off rule. The server
  // refuses it too (403 EXPENSE_SELF_REVIEW); these only hide the controls and explain why.
  const isOwnExpense = (expense) => user?.employeeId != null && expense.employee_id === user.employeeId;
  const canReviewExpense = (expense) => canManageExpenses && (isAdmin || !isOwnExpense(expense));

  const loadExpenses = useCallback(async () => {
    try {
      setLoading(true);
      const params = {};
      if (filterEmployee) params.employee_id = filterEmployee;
      if (filterStatus) params.status = filterStatus;
      if (filterType) params.expense_type = filterType;
      if (filterStartDate) params.start_date = filterStartDate;
      if (filterEndDate) params.end_date = filterEndDate;

      const response = await getExpenses(params);
      setExpenses(response.data);
    } catch (err) {
      setError('Failed to load expenses');
    } finally {
      setLoading(false);
    }
  }, [filterEmployee, filterStatus, filterType, filterStartDate, filterEndDate]);

  const loadEmployees = useCallback(async () => {
    if (canManageExpenses) {
      try {
        const response = await getEmployees();
        setEmployees(response.data);
      } catch (err) {
        console.error('Failed to load employees');
      }
    }
  }, [canManageExpenses]);

  useEffect(() => {
    loadExpenses();
    loadEmployees();
  }, [loadExpenses, loadEmployees]);

  const handleOpenDialog = (expense = null) => {
    if (expense) {
      setEditMode(true);
      setFormData({
        start_date: expense.start_date?.split('T')[0] || '',
        end_date: expense.end_date?.split('T')[0] || '',
        title: expense.title,
        description: expense.description || '',
        expense_type: expense.expense_type
      });
      setCurrentExpense(expense);
    } else {
      setEditMode(false);
      setFormData({
        start_date: new Date().toISOString().split('T')[0],
        end_date: new Date().toISOString().split('T')[0],
        title: '',
        description: '',
        expense_type: 'other'
      });
      setCurrentExpense(null);
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setCurrentExpense(null);
    setEditMode(false);
  };

  const handleSubmit = async () => {
    try {
      if (editMode && currentExpense) {
        await updateExpense(currentExpense.id, formData);
        setSuccess('Expense updated successfully');
      } else {
        await createExpense(formData);
        setSuccess('Expense created successfully');
      }
      handleCloseDialog();
      loadExpenses();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save expense');
    }
  };

  const handleDelete = async (id) => {
    const confirmed = await showConfirm({
      title: 'Delete Expense',
      message: 'Are you sure you want to delete this expense? This action cannot be undone.',
      confirmText: 'Delete',
      confirmColor: 'error',
      severity: 'warning'
    });
    if (!confirmed) return;
    try {
      await deleteExpense(id);
      setSuccess('Expense deleted successfully');
      loadExpenses();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to delete expense');
    }
  };

  const handleStatusChange = async (expense, newStatus) => {
    try {
      await updateExpense(expense.id, { status: newStatus });
      setSuccess(`Expense ${newStatus}`);
      loadExpenses();
    } catch (err) {
      setError('Failed to update status');
    }
  };

  const handleReverseApproval = async (expense) => {
    const confirmed = await showConfirm({
      title: 'Reverse Approval',
      message: 'Are you sure you want to reverse this approval and set status back to pending?',
      confirmText: 'Reverse',
      confirmColor: 'warning',
      severity: 'warning'
    });
    if (!confirmed) return;
    try {
      await updateExpense(expense.id, { status: 'pending' });
      setSuccess('Expense status reversed to pending');
      loadExpenses();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to reverse approval');
    }
  };

  const handleExportPdf = (expenseId) => {
    exportExpensePdf(expenseId);
  };

  const handleMarkPaid = async (expense, isPaid) => {
    try {
      await updateExpense(expense.id, { is_paid: isPaid, status: isPaid ? 'paid' : 'approved' });
      setSuccess(isPaid ? 'Expense marked as paid' : 'Expense unmarked as paid');
      loadExpenses();
      if (currentExpense?.id === expense.id) {
        const refreshed = await getExpense(expense.id);
        setCurrentExpense(refreshed.data);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update payment status');
    }
  };

  const handleExpandExpense = async (expense) => {
    if (expandedId === expense.id) {
      setExpandedId(null);
      setCurrentExpense(null);
    } else {
      try {
        const response = await getExpense(expense.id);
        setCurrentExpense(response.data);
        setExpandedId(expense.id);
      } catch (err) {
        setError('Failed to load expense details');
      }
    }
  };

  // Item management
  const handleOpenItemDialog = (item = null) => {
    if (item) {
      // Edit mode
      setEditItemMode(true);
      setEditingItem(item);
      setItemFormData({
        item_date: item.item_date?.split('T')[0] || new Date().toISOString().split('T')[0],
        category: item.category || 'other',
        description: item.description || '',
        amount: item.amount || '',
        notes: item.notes || '',
        payment_method: item.payment_method || 'personal'
      });
    } else {
      // Add mode
      setEditItemMode(false);
      setEditingItem(null);
      setItemFormData({
        item_date: currentExpense?.start_date?.split('T')[0] || new Date().toISOString().split('T')[0],
        category: 'other',
        description: '',
        amount: '',
        notes: '',
        payment_method: 'personal'
      });
    }
    setOpenItemDialog(true);
  };

  const handleCloseItemDialog = () => {
    setOpenItemDialog(false);
    setEditItemMode(false);
    setEditingItem(null);
  };

  const handleSaveItem = async () => {
    try {
      if (editItemMode && editingItem) {
        await updateExpenseItem(currentExpense.id, editingItem.id, { ...itemFormData, quantity: 1 });
        setSuccess('Item updated successfully');
      } else {
        await addExpenseItem(currentExpense.id, { ...itemFormData, quantity: 1 });
        setSuccess('Item added successfully');
      }
      handleCloseItemDialog();
      // Refresh expense details
      const response = await getExpense(currentExpense.id);
      setCurrentExpense(response.data);
      loadExpenses();
    } catch (err) {
      setError(err.response?.data?.message || (editItemMode ? 'Failed to update item' : 'Failed to add item'));
    }
  };

  const handleDeleteItem = async (itemId) => {
    const confirmed = await showConfirm({
      title: 'Delete Item',
      message: 'Are you sure you want to delete this expense item?',
      confirmText: 'Delete',
      confirmColor: 'error',
      severity: 'warning'
    });
    if (!confirmed) return;
    try {
      await deleteExpenseItem(currentExpense.id, itemId);
      setSuccess('Item deleted');
      const response = await getExpense(currentExpense.id);
      setCurrentExpense(response.data);
      loadExpenses();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to delete item');
    }
  };

  const handleUploadReceipt = async (item, file) => {
    try {
      await uploadReceipt(currentExpense.id, item.id, file);
      setSuccess('Receipt uploaded');
      const response = await getExpense(currentExpense.id);
      setCurrentExpense(response.data);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to upload receipt');
      try {
        const refreshed = await getExpense(currentExpense.id);
        setCurrentExpense(refreshed.data);
        loadExpenses();
      } catch (_) { /* keep current view */ }
    }
  };

  const handleDeleteReceipt = async (item, receiptId = null) => {
    const confirmed = await showConfirm({
      title: 'Delete Receipt',
      message: 'Are you sure you want to delete this receipt?',
      confirmText: 'Delete',
      confirmColor: 'error',
      severity: 'warning'
    });
    if (!confirmed) return;
    try {
      await deleteReceipt(currentExpense.id, item.id, receiptId);
      setSuccess('Receipt deleted');
      const response = await getExpense(currentExpense.id);
      setCurrentExpense(response.data);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to delete receipt');
      try {
        const refreshed = await getExpense(currentExpense.id);
        setCurrentExpense(refreshed.data);
        loadExpenses();
      } catch (_) { /* keep current view */ }
    }
  };

  const handleExport = (detailed = false) => {
    const params = {};
    if (filterEmployee) params.employee_id = filterEmployee;
    if (filterStatus) params.status = filterStatus;
    if (filterType) params.expense_type = filterType;
    if (filterStartDate) params.start_date = filterStartDate;
    if (filterEndDate) params.end_date = filterEndDate;

    if (detailed) {
      exportExpensesDetailed(params);
    } else {
      exportExpenses(params);
    }
  };

  const canEditExpense = (expense) => {
    if (isAdmin) return true;
    // Own rows follow the owner rule (pending only); the server refuses the rest since Gitea #136.
    if (isManager && !isOwnExpense(expense)) return true;
    return expense.employee_id === user?.employeeId && expense.status === 'pending';
  };

  // Calculate totals for current expense
  const lineTotal = (item) => (parseFloat(item.amount) || 0) * (parseFloat(item.quantity ?? 1) || 1);
  const getExpenseTotal = () => {
    if (!currentExpense?.items) return 0;
    return currentExpense.items.reduce((sum, item) => sum + (parseFloat(item.amount) || 0), 0);
  };
  // What the employee is actually owed, versus spend the company already made.
  // Showing only a combined total is how a company-card purchase gets paid twice.
  const getReimbursableTotal = () => (currentExpense?.items || [])
    .filter(i => i.payment_method !== 'company_card')
    .reduce((sum, i) => sum + lineTotal(i), 0);
  const getCompanyCardTotal = () => (currentExpense?.items || [])
    .filter(i => i.payment_method === 'company_card')
    .reduce((sum, i) => sum + lineTotal(i), 0);

  const formatDateRange = (startDate, endDate) => {
    // Parse date strings without timezone conversion
    const formatDate = (dateStr) => {
      if (!dateStr) return '';
      const datePart = dateStr.split('T')[0];
      const [year, month, day] = datePart.split('-');
      return `${month}/${day}/${year}`;
    };
    const start = formatDate(startDate);
    const end = formatDate(endDate);
    return start === end ? start : `${start} - ${end}`;
  };

  const clearFilters = () => {
    setFilterEmployee('');
    setFilterStatus('');
    setFilterType('');
    setFilterStartDate('');
    setFilterEndDate('');
  };

  // Mobile Expense Card
  const renderMobileExpenseCard = (expense) => (
    <Card key={expense.id} variant="outlined" sx={{ mb: 1.5 }}>
      <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 1 }}>
          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            <Typography variant="subtitle1" fontWeight="medium" noWrap>
              {expense.title}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {formatDateRange(expense.start_date, expense.end_date)}
            </Typography>
            {canManageExpenses && expense.employee_name && (
              <Typography variant="caption" color="text.secondary">
                {expense.employee_name}
              </Typography>
            )}
          </Box>
          <Box sx={{ textAlign: 'right' }}>
            <Typography variant="subtitle1" fontWeight="bold">
              ${parseFloat(expense.total_amount || 0).toFixed(2)}
            </Typography>
            <StatusChip status={expense.status} />
          </Box>
        </Box>

        {/* Tags */}
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1.5 }}>
          <Chip 
            label={EXPENSE_TYPES.find(t => t.value === expense.expense_type)?.label || expense.expense_type} 
            size="small" 
            variant="outlined"
          />
          {expense.is_paid && (
            <StatusChip status="paid" />
          )}
        </Box>

        {/* Actions */}
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, borderTop: '1px solid', borderColor: 'divider', pt: 1.5 }}>
          <Button size="small" startIcon={<ExpandMore />} onClick={() => handleExpandExpense(expense)}>
            {expandedId === expense.id ? 'Hide Details' : 'View Details'}
          </Button>
          {canEditExpense(expense) && (
            <Button size="small" startIcon={<Edit />} onClick={() => handleOpenDialog(expense)}>
              Edit
            </Button>
          )}
          {canReviewExpense(expense) && expense.status === 'pending' && (
            <>
              <Button size="small" color="success" startIcon={<CheckCircle />} onClick={() => handleStatusChange(expense, 'approved')}>
                Approve
              </Button>
              <Button size="small" color="error" startIcon={<Cancel />} onClick={() => handleStatusChange(expense, 'rejected')}>
                Reject
              </Button>
            </>
          )}
          {canReviewExpense(expense) && (expense.status === 'approved' || expense.status === 'rejected') && !expense.is_paid && (
            <Button size="small" color="warning" startIcon={<Undo />} onClick={() => handleReverseApproval(expense)}>
              Reverse
            </Button>
          )}
          {canManageExpenses && !canReviewExpense(expense) && (
            <Tooltip title={SELF_REVIEW_TEXT}>
              <InfoOutlined fontSize="small" color="action" sx={{ alignSelf: 'center' }} />
            </Tooltip>
          )}
        </Box>

        {/* Expanded Details */}
        <Collapse in={expandedId === expense.id} timeout="auto" unmountOnExit>
          <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography variant="subtitle2">Line Items</Typography>
              <Box>
                <Button size="small" startIcon={<PictureAsPdf />} onClick={() => handleExportPdf(expense.id)}>
                  PDF
                </Button>
                {canEditExpense(expense) && (expense.status === 'pending' || canManageExpenses) && (
                  <Tooltip title={handPaid(expense) ? ITEM_PAID_LOCK_TEXT : 'Add a line item to this expense'}>
                    <span>
                      <Button size="small" startIcon={<Add />} onClick={() => handleOpenItemDialog()} disabled={handPaid(expense)}>
                        Add Item
                      </Button>
                    </span>
                  </Tooltip>
                )}
              </Box>
            </Box>

            {expense.description && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {expense.description}
              </Typography>
            )}

            {currentExpense?.items?.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
                No items - tap "Add Item" to add expense line items
              </Typography>
            ) : (
              <Stack spacing={1}>
                {currentExpense?.items?.map((item) => (
                  <Card key={item.id} variant="outlined">
                    <CardContent sx={{ py: 1, px: 1.5, '&:last-child': { pb: 1 } }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <Box sx={{ flexGrow: 1 }}>
                          <Typography variant="body2" fontWeight="medium">{item.description}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {item.item_date ? (() => {
                              const datePart = (item.item_date + '').split('T')[0];
                              const [year, month, day] = datePart.split('-');
                              return `${month}/${day}/${year}`;
                            })() : '-'} • {ITEM_CATEGORIES.find(c => c.value === item.category)?.label || item.category}
                          </Typography>
                          {item.payment_method === 'company_card' && (
                            <Chip
                              size="small"
                              variant="outlined"
                              color="info"
                              label="Company card — not reimbursed"
                              sx={{ mt: 0.5, height: 20, fontSize: '0.68rem' }}
                            />
                          )}
                          {item.notes && (
                            <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.5 }}>
                              {item.notes}
                            </Typography>
                          )}
                        </Box>
                        <Box sx={{ textAlign: 'right' }}>
                          <Typography variant="body2" fontWeight="bold">${parseFloat(item.amount).toFixed(2)}</Typography>
                          <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5 }}>
                            {/* Receipts */}
                            {item.receipts && item.receipts.length > 0 && item.receipts.map((receipt) => (
                              <IconButton
                                key={receipt.id}
                                size="small"
                                onClick={() => openReceipt(expense.id, item.id, receipt.id)}
                              >
                                <Receipt color="success" fontSize="small" />
                              </IconButton>
                            ))}
                            {item.receipt_filename && !item.receipts?.length && (
                              <IconButton
                                size="small"
                                onClick={() => openReceipt(expense.id, item.id)}
                              >
                                <Receipt color="success" fontSize="small" />
                              </IconButton>
                            )}
                            {canEditExpense(expense) && (expense.status === 'pending' || canManageExpenses) && (
                              <>
                                <IconButton size="small" component="label" disabled={receiptReplaceLocked(expense, item)}>
                                  <Add fontSize="small" />
                                  <input
                                    type="file"
                                    hidden
                                    accept="image/*,.pdf"
                                    onChange={(e) => {
                                      if (e.target.files[0]) {
                                        handleUploadReceipt(item, e.target.files[0]);
                                      }
                                    }}
                                  />
                                </IconButton>
                                <Tooltip title="Take a photo of the receipt with your camera">
                                  <IconButton size="small" component="label" disabled={receiptReplaceLocked(expense, item)}>
                                    <PhotoCamera fontSize="small" />
                                    <input
                                      type="file"
                                      hidden
                                      accept="image/*"
                                      capture="environment"
                                      onChange={(e) => {
                                        if (e.target.files[0]) {
                                          handleUploadReceipt(item, e.target.files[0]);
                                        }
                                      }}
                                    />
                                  </IconButton>
                                </Tooltip>
                                <IconButton size="small" onClick={() => handleOpenItemDialog(item)}>
                                  <Edit fontSize="small" />
                                </IconButton>
                                <Tooltip title={handPaid(expense) ? ITEM_PAID_LOCK_TEXT : 'Delete Item'}>
                                  <span>
                                    <IconButton size="small" onClick={() => handleDeleteItem(item.id)} disabled={handPaid(expense)}>
                                      <Delete fontSize="small" />
                                    </IconButton>
                                  </span>
                                </Tooltip>
                              </>
                            )}
                          </Box>
                          {receiptReplaceLocked(expense, item) && (
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                              {receiptLockText(expense)}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                ))}
                {currentExpense?.items?.length > 0 && (
                  <Box sx={{ bgcolor: 'grey.100', p: 1.5, borderRadius: 1 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="subtitle2" fontWeight="bold">Total:</Typography>
                      <Typography variant="subtitle2" fontWeight="bold">${getExpenseTotal().toFixed(2)}</Typography>
                    </Box>
                    {getCompanyCardTotal() > 0 && (
                      <>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
                          <Typography variant="caption" color="text.secondary">Owed to employee:</Typography>
                          <Typography variant="caption" fontWeight="bold">${getReimbursableTotal().toFixed(2)}</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography variant="caption" color="text.secondary">On company card:</Typography>
                          <Typography variant="caption">${getCompanyCardTotal().toFixed(2)}</Typography>
                        </Box>
                      </>
                    )}
                  </Box>
                )}
              </Stack>
            )}

            {canReviewExpense(expense) && (
              <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={expense.is_paid}
                      onChange={(e) => handleMarkPaid(expense, e.target.checked)}
                      disabled={expense.status === 'pending' || expense.status === 'rejected' || !!expense.payroll_run_id}
                    />
                  }
                  label={expense.payroll_run_id ? `Paid via payroll run #${expense.payroll_run_id}` : 'Mark as Paid'}
                />
                {expense.payroll_run_id && (
                  <Tooltip title={PAYROLL_LOCK_TEXT}>
                    <InfoOutlined fontSize="small" color="action" />
                  </Tooltip>
                )}
                {!expense.payroll_run_id && expense.is_paid === true && (
                  <Tooltip title={PAID_TICK_LOCK_TEXT}>
                    <InfoOutlined fontSize="small" color="action" />
                  </Tooltip>
                )}
              </Box>
            )}
          </Box>
        </Collapse>
      </CardContent>
    </Card>
  );

  // Mobile View
  const renderMobileView = () => (
    <Stack spacing={1.5}>
      {expenses.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography color="text.secondary">No expenses found</Typography>
        </Box>
      ) : (
        expenses.map(expense => renderMobileExpenseCard(expense))
      )}
    </Stack>
  );

  // Desktop Table View
  const renderDesktopView = () => (
    <TableContainer component={Paper}>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell sx={{ width: 50 }} />
            <TableCell>Date Range</TableCell>
            {canManageExpenses && (
              <TableCell>Employee</TableCell>
            )}
            <TableCell>Title</TableCell>
            <TableCell>Type</TableCell>
            <TableCell>Total</TableCell>
            <TableCell>Status</TableCell>
            <TableCell>
              <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                Paid
                <Tooltip title="Tick once the expense has been reimbursed. HourTrax records the date and who marked it; expand the row to see them. Running payroll marks reimbursed expenses paid automatically. While ticked, the expense cannot be deleted, items cannot be added or deleted, amounts cannot be changed and receipts cannot be deleted; untick it to change them (the untick is recorded in the Transaction Log).">
                  <InfoOutlined fontSize="small" color="action" />
                </Tooltip>
              </Box>
            </TableCell>
            <TableCell>Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {expenses.length === 0 ? (
            <TableRow>
              <TableCell colSpan={canManageExpenses ? 9 : 8} align="center">
                No expenses found
              </TableCell>
            </TableRow>
          ) : (
            expenses.map((expense) => (
              <React.Fragment key={expense.id}>
                <TableRow hover>
                  <TableCell>
                    <IconButton size="small" onClick={() => handleExpandExpense(expense)}>
                      {expandedId === expense.id ? <ExpandLess /> : <ExpandMore />}
                    </IconButton>
                  </TableCell>
                  <TableCell>{formatDateRange(expense.start_date, expense.end_date)}</TableCell>
                  {canManageExpenses && <TableCell>{expense.employee_name}</TableCell>}
                  <TableCell>{expense.title}</TableCell>
                  <TableCell>
                    {EXPENSE_TYPES.find(t => t.value === expense.expense_type)?.label || expense.expense_type}
                  </TableCell>
                  <TableCell>${parseFloat(expense.total_amount || 0).toFixed(2)}</TableCell>
                  <TableCell>
                    <StatusChip status={expense.status} />
                  </TableCell>
                  <TableCell>
                    {canReviewExpense(expense) ? (
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={expense.is_paid}
                              onChange={(e) => handleMarkPaid(expense, e.target.checked)}
                              disabled={expense.status === 'pending' || expense.status === 'rejected' || !!expense.payroll_run_id}
                            />
                          }
                          label=""
                        />
                        {expense.payroll_run_id && (
                          <Tooltip title={PAYROLL_LOCK_TEXT}>
                            <InfoOutlined fontSize="small" color="action" />
                          </Tooltip>
                        )}
                        {!expense.payroll_run_id && expense.is_paid === true && (
                          <Tooltip title={PAID_TICK_LOCK_TEXT}>
                            <InfoOutlined fontSize="small" color="action" />
                          </Tooltip>
                        )}
                      </Box>
                    ) : (
                      expense.is_paid ? <CheckCircle color="success" /> : '-'
                    )}
                  </TableCell>
                  <TableCell>
                    {canEditExpense(expense) && (
                      <>
                        <IconButton size="small" onClick={() => handleOpenDialog(expense)}>
                          <Edit />
                        </IconButton>
                        {(isAdmin || expense.status === 'pending') && !expense.payroll_run_id && (
                          <Tooltip title={handPaid(expense) ? EXPENSE_PAID_DELETE_TEXT : 'Delete'}>
                            <span>
                              <IconButton size="small" onClick={() => handleDelete(expense.id)} disabled={handPaid(expense)}>
                                <Delete />
                              </IconButton>
                            </span>
                          </Tooltip>
                        )}
                      </>
                    )}
                    {canReviewExpense(expense) && expense.status === 'pending' && (
                      <>
                        <Tooltip title="Approve">
                          <IconButton
                            size="small"
                            color="success"
                            onClick={() => handleStatusChange(expense, 'approved')}
                          >
                            <CheckCircle />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Reject">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleStatusChange(expense, 'rejected')}
                          >
                            <Cancel />
                          </IconButton>
                        </Tooltip>
                      </>
                    )}
                    {canReviewExpense(expense) && (expense.status === 'approved' || expense.status === 'rejected') && !expense.is_paid && (
                      <Tooltip title="Reverse to Pending">
                        <IconButton
                          size="small"
                          color="warning"
                          onClick={() => handleReverseApproval(expense)}
                        >
                          <Undo />
                        </IconButton>
                      </Tooltip>
                    )}
                    {canManageExpenses && !canReviewExpense(expense) && (
                      <Tooltip title={SELF_REVIEW_TEXT}>
                        <InfoOutlined fontSize="small" color="action" sx={{ verticalAlign: 'middle', ml: 0.5 }} />
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
                
                {/* Expanded Row - Expense Details */}
                <TableRow>
                  <TableCell colSpan={canManageExpenses ? 9 : 8} sx={{ py: 0 }}>
                    <Collapse in={expandedId === expense.id} timeout="auto" unmountOnExit>
                      <Box sx={{ p: 2 }}>
                        <Card variant="outlined">
                          <CardContent>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                              <Typography variant="h6">Line Items</Typography>
                              <Box>
                                <Button
                                  size="small"
                                  variant="outlined"
                                  startIcon={<PictureAsPdf />}
                                  onClick={() => handleExportPdf(expense.id)}
                                  sx={{ mr: 1 }}
                                >
                                  Export PDF
                                </Button>
                                {canEditExpense(expense) && (expense.status === 'pending' || canManageExpenses) && (
                                  <Tooltip title={handPaid(expense) ? ITEM_PAID_LOCK_TEXT : 'Add a line item to this expense'}>
                                    <span>
                                      <Button
                                        size="small"
                                        variant="contained"
                                        startIcon={<Add />}
                                        onClick={() => handleOpenItemDialog()}
                                        disabled={handPaid(expense)}
                                      >
                                        Add Item
                                      </Button>
                                    </span>
                                  </Tooltip>
                                )}
                              </Box>
                            </Box>

                            {expense.description && (
                              <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                                {expense.description}
                              </Typography>
                            )}

                            <Table size="small">
                              <TableHead>
                                <TableRow>
                                  <TableCell>Date</TableCell>
                                  <TableCell>Category</TableCell>
                                  <TableCell>Description</TableCell>
                                  <TableCell>Notes</TableCell>
                                  <TableCell align="right">Amount</TableCell>
                                  <TableCell>Receipt</TableCell>
                                  <TableCell>Actions</TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {currentExpense?.items?.length === 0 ? (
                                  <TableRow>
                                    <TableCell colSpan={7} align="center">
                                      No items - click "Add Item" to add expense line items
                                    </TableCell>
                                  </TableRow>
                                ) : (
                                  currentExpense?.items?.map((item) => (
                                    <TableRow key={item.id}>
                                      <TableCell>
                                        {item.item_date ? (() => {
                                          const datePart = (item.item_date + '').split('T')[0];
                                          const [year, month, day] = datePart.split('-');
                                          return `${month}/${day}/${year}`;
                                        })() : '-'}
                                      </TableCell>
                                      <TableCell>
                                        {ITEM_CATEGORIES.find(c => c.value === item.category)?.label || item.category}
                                      </TableCell>
                                      <TableCell>{item.description}</TableCell>
                                      <TableCell>
                                        {item.notes ? (
                                          <Tooltip title={item.notes}>
                                            <Typography variant="body2" sx={{ maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', cursor: 'help' }}>
                                              {item.notes}
                                            </Typography>
                                          </Tooltip>
                                        ) : '-'}
                                      </TableCell>
                                      <TableCell align="right">${parseFloat(item.amount).toFixed(2)}</TableCell>
                                      <TableCell>
                                        <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.5 }}>
                                          {/* Display existing receipts */}
                                          {item.receipts && item.receipts.length > 0 ? (
                                            item.receipts.map((receipt, idx) => (
                                              <Box key={receipt.id} sx={{ display: 'flex', alignItems: 'center', bgcolor: 'grey.100', borderRadius: 1, px: 0.5 }}>
                                                <Tooltip title={`View: ${receipt.original_filename}`}>
                                                  <IconButton
                                                    size="small"
                                                    onClick={() => openReceipt(expense.id, item.id, receipt.id)}
                                                    sx={{ p: 0.5 }}
                                                  >
                                                    <Receipt color="success" fontSize="small" />
                                                  </IconButton>
                                                </Tooltip>
                                                {canEditExpense(expense) && (expense.status === 'pending' || canManageExpenses) && (
                                                  receiptLockText(expense) ? (
                                                    <>
                                                      <Tooltip title={receiptLockText(expense)}>
                                                        <span>
                                                          <IconButton size="small" disabled sx={{ p: 0.5 }}>
                                                            <Delete fontSize="small" />
                                                          </IconButton>
                                                        </span>
                                                      </Tooltip>
                                                      <Tooltip title={receiptLockText(expense)}>
                                                        <InfoOutlined fontSize="small" color="action" />
                                                      </Tooltip>
                                                    </>
                                                  ) : (
                                                    <Tooltip title="Delete">
                                                      <IconButton
                                                        size="small"
                                                        onClick={() => handleDeleteReceipt(item, receipt.id)}
                                                        sx={{ p: 0.5 }}
                                                      >
                                                        <Delete fontSize="small" color="error" />
                                                      </IconButton>
                                                    </Tooltip>
                                                  )
                                                )}
                                              </Box>
                                            ))
                                          ) : item.receipt_filename ? (
                                            /* Legacy single receipt support */
                                            <Box sx={{ display: 'flex', alignItems: 'center', bgcolor: 'grey.100', borderRadius: 1, px: 0.5 }}>
                                              <Tooltip title={`View: ${item.receipt_filename}`}>
                                                <IconButton
                                                  size="small"
                                                  onClick={() => openReceipt(expense.id, item.id)}
                                                  sx={{ p: 0.5 }}
                                                >
                                                  <Receipt color="success" fontSize="small" />
                                                </IconButton>
                                              </Tooltip>
                                              {canEditExpense(expense) && (expense.status === 'pending' || canManageExpenses) && (
                                                receiptLockText(expense) ? (
                                                  <>
                                                    <Tooltip title={receiptLockText(expense)}>
                                                      <span>
                                                        <IconButton size="small" disabled sx={{ p: 0.5 }}>
                                                          <Delete fontSize="small" />
                                                        </IconButton>
                                                      </span>
                                                    </Tooltip>
                                                    <Tooltip title={receiptLockText(expense)}>
                                                      <InfoOutlined fontSize="small" color="action" />
                                                    </Tooltip>
                                                  </>
                                                ) : (
                                                  <Tooltip title="Delete">
                                                    <IconButton
                                                      size="small"
                                                      onClick={() => handleDeleteReceipt(item)}
                                                      sx={{ p: 0.5 }}
                                                    >
                                                      <Delete fontSize="small" color="error" />
                                                    </IconButton>
                                                  </Tooltip>
                                                )
                                              )}
                                            </Box>
                                          ) : null}
                                          {/* Add receipt button */}
                                          {canEditExpense(expense) && (expense.status === 'pending' || canManageExpenses) && (
                                            <>
                                            <Tooltip title={receiptReplaceLocked(expense, item) ? receiptLockText(expense) : 'Add Receipt'}>
                                              <span>
                                              <IconButton
                                                size="small"
                                                component="label"
                                                disabled={receiptReplaceLocked(expense, item)}
                                                sx={{ p: 0.5 }}
                                              >
                                                <Add fontSize="small" />
                                                <input
                                                  type="file"
                                                  hidden
                                                  accept="image/*,.pdf"
                                                  onChange={(e) => {
                                                    if (e.target.files[0]) {
                                                      handleUploadReceipt(item, e.target.files[0]);
                                                    }
                                                  }}
                                                />
                                              </IconButton>
                                              </span>
                                            </Tooltip>
                                            <Tooltip title={receiptReplaceLocked(expense, item) ? receiptLockText(expense) : 'Take a photo of the receipt with your camera'}>
                                              <span>
                                              <IconButton
                                                size="small"
                                                component="label"
                                                disabled={receiptReplaceLocked(expense, item)}
                                                sx={{ p: 0.5 }}
                                              >
                                                <PhotoCamera fontSize="small" />
                                                <input
                                                  type="file"
                                                  hidden
                                                  accept="image/*"
                                                  capture="environment"
                                                  onChange={(e) => {
                                                    if (e.target.files[0]) {
                                                      handleUploadReceipt(item, e.target.files[0]);
                                                    }
                                                  }}
                                                />
                                              </IconButton>
                                              </span>
                                            </Tooltip>
                                            </>
                                          )}
                                          {/* Show dash if no receipts and not editable */}
                                          {(!item.receipts || item.receipts.length === 0) && !item.receipt_filename && 
                                           !(canEditExpense(expense) && (expense.status === 'pending' || canManageExpenses)) && '-'}
                                        </Box>
                                      </TableCell>
                                      <TableCell>
                                        {canEditExpense(expense) && (expense.status === 'pending' || canManageExpenses) && (
                                          <>
                                            <Tooltip title="Edit Item">
                                              <IconButton
                                                size="small"
                                                onClick={() => handleOpenItemDialog(item)}
                                              >
                                                <Edit />
                                              </IconButton>
                                            </Tooltip>
                                            <Tooltip title={handPaid(expense) ? ITEM_PAID_LOCK_TEXT : 'Delete Item'}>
                                              <span>
                                                <IconButton
                                                  size="small"
                                                  onClick={() => handleDeleteItem(item.id)}
                                                  disabled={handPaid(expense)}
                                                >
                                                  <Delete />
                                                </IconButton>
                                              </span>
                                            </Tooltip>
                                          </>
                                        )}
                                      </TableCell>
                                    </TableRow>
                                  ))
                                )}
                                {currentExpense?.items?.length > 0 && (
                                  <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                                    <TableCell colSpan={4} align="right">
                                      <Typography variant="subtitle2" fontWeight="bold">
                                        Total:
                                      </Typography>
                                    </TableCell>
                                    <TableCell align="right">
                                      <Typography variant="subtitle2" fontWeight="bold">
                                        ${getExpenseTotal().toFixed(2)}
                                      </Typography>
                                    </TableCell>
                                    <TableCell colSpan={2} />
                                  </TableRow>
                                )}
                              </TableBody>
                            </Table>

                            {expense.manager_notes && (
                              <Box sx={{ mt: 2 }}>
                                <Divider sx={{ mb: 1 }} />
                                <Typography variant="subtitle2">Manager Notes:</Typography>
                                <Typography variant="body2">{expense.manager_notes}</Typography>
                              </Box>
                            )}

                            {expense.is_paid && expense.paid_date && (
                              <Box sx={{ mt: 2 }}>
                                <Typography variant="body2" color="success.main">
                                  Paid on {expense.paid_date?.split('T')[0].split('-').slice(1).concat(expense.paid_date?.split('T')[0].split('-')[0]).join('/')}{expense.paid_by_name ? ` by ${expense.paid_by_name}` : ''}{expense.payroll_run_id ? ` (payroll run #${expense.payroll_run_id})` : ''}
                                </Typography>
                              </Box>
                            )}
                          </CardContent>
                        </Card>
                      </Box>
                    </Collapse>
                  </TableCell>
                </TableRow>
              </React.Fragment>
            ))
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );

  return (
    <Box sx={{ pb: isMobile ? 10 : 0 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 1 }}>
        <Typography variant="h5">Expense Reimbursements</Typography>
        {!isMobile && (
          <Box>
            {canManageExpenses && (
              <>
                <Button
                  variant="outlined"
                  startIcon={<Download />}
                  onClick={() => handleExport(false)}
                  sx={{ mr: 1 }}
                >
                  Export Summary
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<Download />}
                  onClick={() => handleExport(true)}
                  sx={{ mr: 1 }}
                >
                  Export Detailed
                </Button>
              </>
            )}
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => handleOpenDialog()}
            >
              New Expense
            </Button>
          </Box>
        )}
        {isMobile && (
          <Button
            variant="outlined"
            startIcon={<FilterList />}
            onClick={() => setShowFilters(!showFilters)}
            size="small"
          >
            Filters
          </Button>
        )}
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}

      {/* Filters */}
      <Collapse in={!isMobile || showFilters}>
        <Paper sx={{ p: 2, mb: 3 }}>
          <Grid container spacing={2} alignItems="center">
            {canManageExpenses && (
              <Grid item xs={12} sm={6} md={2}>
                <FormControl fullWidth size="small">
                  <InputLabel>Employee</InputLabel>
                  <Select
                    value={filterEmployee}
                    label="Employee"
                    onChange={(e) => setFilterEmployee(e.target.value)}
                  >
                    <MenuItem value="">All</MenuItem>
                    {employees.map((emp) => (
                      <MenuItem key={emp.id} value={emp.id}>{emp.first_name} {emp.last_name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            )}
            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select
                  value={filterStatus}
                  label="Status"
                  onChange={(e) => setFilterStatus(e.target.value)}
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="pending">Pending</MenuItem>
                  <MenuItem value="approved">Approved</MenuItem>
                  <MenuItem value="rejected">Rejected</MenuItem>
                  <MenuItem value="paid">Paid</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Type</InputLabel>
                <Select
                  value={filterType}
                  label="Type"
                  onChange={(e) => setFilterType(e.target.value)}
                >
                  <MenuItem value="">All</MenuItem>
                  {EXPENSE_TYPES.map((type) => (
                    <MenuItem key={type.value} value={type.value}>{type.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <TextField
                fullWidth
                size="small"
                type="date"
                label="Start Date"
                value={filterStartDate}
                onChange={(e) => setFilterStartDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <TextField
                fullWidth
                size="small"
                type="date"
                label="End Date"
                value={filterEndDate}
                onChange={(e) => setFilterEndDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <Button variant="outlined" onClick={clearFilters} fullWidth={isMobile}>
                Clear Filters
              </Button>
            </Grid>
          </Grid>
        </Paper>
      </Collapse>

      {/* Expenses List */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : isMobile ? (
        renderMobileView()
      ) : (
        renderDesktopView()
      )}

      {/* Mobile FAB */}
      {isMobile && (
        <Fab
          color="primary"
          aria-label="new expense"
          onClick={() => handleOpenDialog()}
          sx={{
            position: 'fixed',
            bottom: 80,
            right: 16,
            zIndex: 1000,
          }}
        >
          <Add />
        </Fab>
      )}

      <ExpenseFormDialog
        open={openDialog}
        onClose={handleCloseDialog}
        onSubmit={handleSubmit}
        editMode={editMode}
        formData={formData}
        setFormData={setFormData}
        canManageExpenses={canManageExpenses}
        currentExpense={currentExpense}
        expenseTypes={EXPENSE_TYPES}
      />

      <ExpenseItemDialog
        open={openItemDialog}
        onClose={handleCloseItemDialog}
        onSave={handleSaveItem}
        editItemMode={editItemMode}
        itemFormData={itemFormData}
        setItemFormData={setItemFormData}
        itemCategories={ITEM_CATEGORIES}
        moneyLocked={editItemMode && handPaid(currentExpense)}
      />

      <ConfirmDialogComponent />
    </Box>
  );
}

export default Expenses;
